import type { ArbitrumVerdict } from "../arbitrum/types";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type {
  TiltScoutAxisScore,
  TiltScoutInstrumentOutlook,
  TiltScoutSnapshot,
} from "./tilt-scout-types";
import { headlineWeightForAlert } from "./tilt-scout-headline-weight";
import { alertText } from "./tilt-scout-symbols";
import { sovereignCreditDirection } from "./tilt-scout-credit-direction";

export function scoreOutlook(outlook: TiltScoutInstrumentOutlook | null) {
  if (!outlook) return 0;
  const lean = leanDirection(outlook.lean);
  if (lean === 0) return 0;
  const ivScore = safeNumber(outlook.ivScore);
  return lean * Math.min(6, 2.5 + ivScore * 0.45);
}

export function scoreRiskFlow(alerts: RiskFlowAlert[]) {
  if (alerts.length === 0) return 0;
  let weighted = 0;
  let weight = 0;

  for (const alert of alerts.slice(0, 5)) {
    const direction = alertDirection(alert);
    if (direction === 0) continue;
    const itemWeight = headlineWeightForAlert(alert).headlineImpactWeight;
    weighted += direction * itemWeight;
    weight += itemWeight;
  }

  if (weight === 0) return 0;
  return clamp((weighted / weight) * 3, -3, 3);
}

export function buildAxisScores(alerts: RiskFlowAlert[]): TiltScoutAxisScore[] {
  return [
    buildAxisScore("econ", "Econ", alerts.filter(isEconAlert)),
    buildAxisScore(
      "geopolitical",
      "Geopolitical",
      alerts.filter(isGeopoliticalAlert),
    ),
    buildAxisScore("monetary", "Monetary", alerts.filter(isMonetaryAlert)),
  ];
}

export function withOverallAxis(
  axes: TiltScoutAxisScore[],
  score: number,
  fallbackCount: number,
) {
  return [
    ...axes,
    {
      id: "overall" as const,
      label: "Overall",
      score,
      posture: scorePosture(score),
      sourceCount:
        axes.reduce((total, axis) => total + axis.sourceCount, 0) ||
        fallbackCount,
    },
  ];
}

export function scoreChamber(verdict: ArbitrumVerdict | null) {
  if (!verdict) return 0;
  const direction = textDirection(
    `${verdict.question ?? ""} ${verdict.digest_text ?? ""}`,
  );
  if (direction === 0) return 0;
  return direction * Math.min(1.8, safeNumber(verdict.confidence) * 1.8);
}

export function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function roundScore(value: number) {
  return Math.round(value * 10) / 10;
}

function buildAxisScore(
  id: TiltScoutAxisScore["id"],
  label: string,
  alerts: RiskFlowAlert[],
): TiltScoutAxisScore {
  const score = roundScore(scoreRiskFlow(alerts));
  return {
    id,
    label,
    score,
    posture: scorePosture(score),
    sourceCount: alerts.length,
  };
}

function scorePosture(score: number): TiltScoutSnapshot["posture"] {
  if (score >= 2) return "bullish";
  if (score <= -2) return "bearish";
  return "neutral";
}

function isEconAlert(alert: RiskFlowAlert) {
  const text = alertText(alert);
  return (
    alert.source === "economic-calendar" ||
    Boolean(alert.econData) ||
    alert.riskType === "Macro" ||
    alert.riskType === "Credit" ||
    /\b(cpi|ppi|pce|nfp|payroll|jobs?|claims|gdp|ism|pmi|inflation|retail sales|trade balance|trade agreement|trade deal|exports?|imports?|foreign net investment|portfolio flows?|capital (?:inflow|outflow)|currency market|weak won|cftc)\b/.test(
      text,
    )
  );
}

function isGeopoliticalAlert(alert: RiskFlowAlert) {
  if (alert.riskType === "Credit") return false;
  const text = alertText(alert);
  return (
    alert.riskType === "Geopolitical" ||
    alert.source === "osint-sources" ||
    /\b(war|missile|drone|iran|israel|china|taiwan|russia|ukraine|nato|lithuania|vilnius|baltic|poland|latvia|estonia|airspace|strait|hormuz|ukmto|yemen|red sea|gulf of aden|maritime incident|commercial vessels?|shipping lanes?|mines?|mining|tariff|sanction|export controls?|trade agreement|trade deal|ceasefire|peace deal|peace agreement|agreement in place|mediator|mediation|signing ceremony|geopolit|opec)\b/.test(
      text,
    )
  );
}

function isMonetaryAlert(alert: RiskFlowAlert) {
  const text = alertText(alert);
  return (
    alert.riskType === "Liquidity" ||
    /\b(fed|fomc|powell|pboc|central bank|rate|cuts?|hikes?|yields?|treasury|dollar|dxy|yuan|won|currency|fx|repo|reverse repo|liquidity|credit|minutes|balance sheet|midpoint|fix(?:es|ed|ing)?)\b/.test(
      text,
    )
  );
}

function leanDirection(lean?: string) {
  const value = (lean ?? "").toLowerCase();
  if (value.includes("bull")) return 1;
  if (value.includes("bear")) return -1;
  return 0;
}

function alertDirection(alert: RiskFlowAlert) {
  const text = `${alert.headline} ${alert.summary}`;
  const headline = headlineDirection(text);
  if (headline !== null) return headline;
  if (alert.riskType !== "Credit" && isGeopoliticalText(text)) return 0;
  return leanDirection(alert.direction ?? "");
}

function textDirection(value: string) {
  return headlineDirection(value) ?? 0;
}

function headlineDirection(value: string): -1 | 0 | 1 | null {
  const text = value.toLowerCase();
  const monetaryDirection = monetaryPolicyDirection(text);
  if (monetaryDirection != null) return monetaryDirection;
  const creditDirection = sovereignCreditDirection(text);
  if (creditDirection !== 0) return creditDirection;
  if (isConfirmedGeopoliticalRelief(text)) return 1;
  if (isDealFragilityBearish(text)) return -1;
  if (isMaritimeThreatBearish(text)) return -1;
  if (isOfficialDealEndorsementRelief(text)) return 1;

  if (
    /\b((?:cites?\s+source|source\s+says|sources?\s+say|according\s+to|reportedly).{0,120}(?:ceasefire|agreement|deal|mou|memorandum|sanctions?|blockade|navigation|hormuz|reopen|opening|assets?|funds?)|(?:ceasefire|agreement|deal|mou|memorandum|sanctions?|blockade|navigation|hormuz|reopen|opening|assets?|funds?).{0,120}(?:cites?\s+source|source\s+says|sources?\s+say|according\s+to|reportedly)|(?:on\s+mou|under\s+mou).{0,120}(?:blockade|navigation|hormuz|sanctions?|assets?|funds?|reopen|opening|removal|lifting)|(?:blockade|navigation|hormuz|sanctions?|assets?|funds?|reopen|opening|removal|lifting).{0,120}(?:on\s+mou|under\s+mou))\b/.test(
      text,
    ) ||
    /\b((?:prepared\s+to|urge|urges|must|crucial|conditions?|commitments?|requires?|draft|demands?|proposal|proposed|necessity|hope|hopes|if|talks?\s+to\s+(?:start|begin|cover|address)|negotiations?\s+to\s+(?:start|begin)|won(?:'|’)t\s+start|will\s+be\s+announced|to\s+be\s+(?:lifted|dropped|removed|eased|waived)).{0,90}(?:ceasefire|agreement|deal|memorandum|mou|sanctions?|blockade|navigation|hormuz|funds?|assets?|nuclear|missile|reconstruction|oversight|war|conflict)|(?:ceasefire|agreement|deal|memorandum|mou|sanctions?|blockade|navigation|hormuz|funds?|assets?|nuclear|missile|reconstruction|oversight|war|conflict).{0,90}(?:prepared\s+to|urge|urges|must|crucial|conditions?|commitments?|requires?|draft|demands?|proposal|proposed|necessity|hope|hopes|if|talks?|negotiations?|won(?:'|’)t\s+start|will\s+be\s+announced|to\s+be\s+(?:lifted|dropped|removed|eased|waived)))\b/.test(
      text,
    )
  )
    return 0;
  if (
    /\b(false\s+alarm|no\s+(?:damage|injur(?:y|ies)|data\s+breach|land\s+explosions?|environmental\s+harm)|crew\s+safe|no\s+official\s+confirmation)\b/.test(
      text,
    ) ||
    /\b(no|not|never|without)\b.{0,56}(?:assets?|funds?|sanctions?|blockade|ceasefire|deal|agreement|strait|hormuz|navigation).{0,56}(?:released?|unfrozen|lift(?:ed)?|removed?|eased?|signed|implemented|reopened?|moved|transferred)\b/.test(
      text,
    ) ||
    /\b(?:prevent|prevents|prevented|preventing|block|blocks|blocked|blocking).{0,56}(?:unfreez|release|transfer).{0,56}(?:assets?|funds?)\b/.test(
      text,
    )
  )
    return 0;
  if (
    /\b(finger\s+on\s+the\s+trigger|enemy\s+mistake.{0,48}unforgettable\s+lesson|unforgettable\s+lesson|strong\s+response|supply\s+shock|price\s+shock|energy[-\s]driven\s+price\s+shock|(?:nato|lithuania|lithuanian|vilnius|baltic|poland|polish|latvia|latvian|estonia|estonian).{0,140}(?:military\s+drone|suspected\s+drone|drone|aircraft|missile).{0,120}(?:airspace|nato\s+jets?\s+deployed|jets?\s+deployed|air\s+defen[cs]e|intercept(?:ed|ion)?|scrambl(?:e|ed)|alert\s+to\s+residents?)|(?:ukraine|ukrainian|russia|russian|kyiv|kiev|kharkiv|moscow|tula).{0,96}(?:air\s+raids?|air\s+strikes?|aerial\s+strike|drone\s+strikes?|missile\s+strikes?|killed|injured|hurt|ablaze|without\s+power|electricity\s+lines\s+damaged)|(?:air\s+raids?|air\s+strikes?|aerial\s+strike|drone\s+strikes?|missile\s+strikes?|killed|injured|hurt|ablaze|without\s+power|electricity\s+lines\s+damaged).{0,96}(?:ukraine|ukrainian|russia|russian|kyiv|kiev|kharkiv|moscow|tula)|retaliat\w*|won(?:'|’)t\s+go\s+unanswered|will\s+not\s+go\s+unanswered|ceasefire\s+breach|breach(?:ed)?\s+ceasefire|fired?.{0,40}projectiles?|projectiles?.{0,40}fired?|defensive\s+and\s+offensive\s+operations|offensive\s+operations|readying\s+for\s+potential\s+fire|potential\s+fire\s+toward|air\s+raid\s+sirens?|sirens?\s+(?:sounded|activated)|hostile\s+aircraft\s+infiltration|enemy\s+aircraft\s+infiltration|(?:not\s+to\s+restore|will\s+not\s+return|not\s+return).{0,80}(?:hormuz|strait).{0,80}pre-war|(?:hormuz|strait).{0,80}(?:not\s+to\s+restore|will\s+not\s+return|not\s+return).{0,80}pre-war|threat\s+to\s+commercial\s+(?:traffic|vessels|shipping))\b/.test(
      text,
    )
  )
    return -1;
  if (
    /\b(bull(?:ish)?|rally|risk-on|dovish|cuts?|soft landing|goldilocks|bid|de-escalat\w*|truce|breakthrough)\b/.test(
      text,
    ) ||
    /\bceasefire\s+(holds?|held|reached|agreed|confirmed|implemented)\b/.test(
      text,
    ) ||
    /\b(blockade.{0,56}(?:to\s+|will\s+|will\s+start\s+|start(?:s|ed)?\s+)?(?:end|ending|lift|remov)|(?:end|ending|removal|lifting)\s+of\s+(?:u\.s\.|us|united\s+states)?\s*(?:naval\s+)?blockade|sanctions?.{0,32}(?:lift|drop|remov|eas)|hormuz.{0,48}reopen|reopen.{0,48}hormuz|(?:opening|reopening)\s+of\s+(?:the\s+)?strait\s+of\s+hormuz|authori[sz](?:e|es|ed).{0,80}(?:opening|reopening|removal|end).{0,60}(?:strait|hormuz|blockade)|agreement\s+sign(?:ed|ing)|deal\s+(?:reached|signed|agreed|announced|finali[sz]ed)|(?:deal|agreement|signing).{0,80}still\s+on\s+track|still\s+on\s+track.{0,80}(?:deal|agreement|ending\s+the\s+war)|welcomes?.{0,40}(?:deal|agreement|memorandum|ceasefire)|(?:announce|declared?|declare).{0,80}(?:immediate,\s*)?permanent\s+end\s+to\s+(?:war|military\s+(?:operations|actions))|(?:immediate,\s*)?permanent\s+end\s+to\s+(?:war|military\s+(?:operations|actions))|war\s+(?:concludes?|ends?|over)|(?:assets|funds).{0,40}(?:unfrozen|freed|released)|navigation\s+(?:freedom|restored|reopened))\b/.test(
      text,
    )
  )
    return 1;
  if (
    /\b(bear(?:ish)?|sticky|hawkish|spike|stress|selloff|tariff|escalat\w*|risk-off|violat\w*|collaps\w*|reject\w*)\b/.test(
      text,
    )
  )
    return -1;
  return null;
}

function isConfirmedGeopoliticalRelief(text: string) {
  if (
    /\b(cites?\s+source|source\s+says|sources?\s+say|according\s+to|reportedly|draft|proposal|proposed|mou|memorandum|would\s+(?:feature|include|require|involve|see)|to\s+be\s+(?:lifted|dropped|removed|eased|waived)|will\s+be\s+announced)\b/.test(
      text,
    )
  )
    return false;
  return /\b(agreement\s+(?:reached|agreed|announced|finali[sz]ed|implemented|in\s+place)|peace\s+agreement\s+(?:reached|agreed|signed|announced|finali[sz]ed|implemented|in\s+place)|deal\s+(?:reached|signed|agreed|announced|finali[sz]ed|implemented|in\s+place)|deal.{0,40}is\s+now\s+complete|(?:deal|agreement|accord).{0,100}(?:first\s+and\s+important\s+step|important\s+step|step\s+toward|ensur(?:e|es|ed|ing)|in\s+which).{0,100}(?:do\s+not|does\s+not|won(?:'|’)t|will\s+not|not\s+to|never|prevent(?:s|ed|ing)?).{0,80}(?:get|obtain|acquire|develop|have)?\s*(?:a\s+)?nuclear\s+weapon|(?:announce|announced|declared?|declare).{0,80}(?:immediate,\s*)?permanent\s+halt\s+to\s+(?:war|hostilities|fighting|military\s+(?:operations|actions))|(?:immediate,\s*)?permanent\s+halt\s+to\s+(?:war|hostilities|fighting|military\s+(?:operations|actions)))\b/.test(
    text,
  );
}

function isOfficialDealEndorsementRelief(text: string) {
  return /\b(congratulat\w*.{0,80}(?:iran\s+deal|deal|agreement|mou|memorandum)|welcomes?.{0,80}(?:iran\s+deal|deal|agreement|mou|memorandum|ceasefire))\b/.test(
    text,
  );
}

function isDealFragilityBearish(text: string) {
  return /\b((?:not\s+bound\s+by|won(?:'|’)t\s+be\s+bound\s+by).{0,80}(?:iran|u\.?s\.?|us)?.{0,80}(?:deal|agreement|mou)|(?:peace\s+deal|peace\s+agreement|ceasefire|truce|agreement|deal|mou|memorandum).{0,48}(?:unclear|uncertain|in\s+doubt|not\s+clear|no\s+clarity)|(?:unclear|uncertain|in\s+doubt|not\s+clear|no\s+clarity).{0,48}(?:peace\s+deal|peace\s+agreement|ceasefire|truce|agreement|deal|mou|memorandum)|(?:must\s+not|should\s+not|cannot)\s+compromise.{0,80}(?:dismantl\w*|hezbollah)|(?:idf|israel(?:'|’)?s\s+defen[cs]e\s+minister|netanyahu).{0,100}(?:remain|stay).{0,100}(?:security\s+zones?|lebanon|syria|gaza)|(?:area|zone).{0,80}(?:cleared|evacuated).{0,100}(?:residents?|terrorist\s+infrastructure|contact\s+villages?)|oil\s+supply.{0,80}(?:take|takes).{0,40}months?\s+to\s+recover)\b/.test(
    text,
  );
}

function isMaritimeThreatBearish(text: string) {
  return /\b((?:ukmto|maritime|shipping|commercial\s+vessels?|cargo\s+vessels?).{0,120}(?:incident|attack|threat|mines?|mining).{0,120}(?:yemen|red\s+sea|gulf\s+of\s+aden|oman|hormuz|strait)|(?:incident|attack).{0,80}(?:off|near|south\s+off|north\s+off|east\s+off|west\s+off).{0,80}(?:yemen|red\s+sea|gulf\s+of\s+aden|oman|hormuz|strait)|(?:threat(?:s)?\s+of\s+mines?|mines?|mining).{0,120}(?:hormuz|strait).{0,120}(?:navigation|concern|shipping|vessels?|commercial\s+traffic)?|(?:hormuz|strait).{0,120}(?:threat(?:s)?\s+of\s+mines?|mines?|mining|navigation\s+concern))\b/.test(
    text,
  );
}

function monetaryPolicyDirection(text: string): -1 | 0 | 1 | null {
  const hikeBets =
    /\b(?:fed\s+)?(?:rate\s+)?hike\s+(?:bets?|odds|expectations?|pricing|probabilit(?:y|ies)|chances?)\b/i;
  const cutBets =
    /\b(?:fed\s+)?(?:rate\s+)?cut\s+(?:bets?|odds|expectations?|pricing|probabilit(?:y|ies)|chances?)\b/i;
  const pared =
    /\b(pare[sd]?|paring|trim(?:s|med|ming)?|cut(?:s|ting)?|reduc(?:e|es|ed|ing)|lower(?:s|ed|ing)?|scale[sd]?\s+back|fad(?:e|es|ed|ing)|eas(?:e|es|ed|ing)|dial(?:s|ed|ing)?\s+back)\b/i;
  const added =
    /\b(add(?:s|ed|ing)?|boost(?:s|ed|ing)?|rais(?:e|es|ed|ing)|increase(?:s|d|ing)?|price[sd]?\s+in|build(?:s|ing)?|climb(?:s|ed|ing)?|rise(?:s|n|ing)?|jump(?:s|ed|ing)?)\b/i;

  if (hasNearPolicyText(text, pared, hikeBets)) return 1;
  if (hasNearPolicyText(text, added, hikeBets)) return -1;
  if (hasNearPolicyText(text, added, cutBets)) return 1;
  if (hasNearPolicyText(text, pared, cutBets)) return -1;
  return null;
}

function hasNearPolicyText(text: string, first: RegExp, second: RegExp) {
  return (
    nearPolicyText(text, first, second) || nearPolicyText(text, second, first)
  );
}

function nearPolicyText(text: string, first: RegExp, second: RegExp) {
  const match = first.exec(text);
  if (!match || match.index == null) return false;
  const start = Math.max(0, match.index - 120);
  const end = Math.min(text.length, match.index + match[0].length + 120);
  return second.test(text.slice(start, end));
}

function isGeopoliticalText(value: string) {
  return /\b(iran|israel|hormuz|blockade|ceasefire|truce|war|missile|strike|sanctions?|navigation|naval|ukmto|yemen|red\s+sea|gulf\s+of\s+aden|commercial\s+vessels?|maritime\s+incident|shipping\s+lanes?|mines?|mining|geopolit|taiwan|ukraine|russia|china|nato|lithuania|vilnius|baltic|poland|latvia|estonia|airspace|military\s+drone)\b/i.test(
    value,
  );
}

function safeNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
